import { useForm, Link } from '@inertiajs/react'
import { type FormEvent } from 'react'
import PublicLayout from '@/layouts/PublicLayout'
import SeoHead from '@/components/SeoHead'

interface Props {
  legal_basis_options: Record<string, string>
  details_max_length: number
  values?: Partial<FormData>
  errors?: Record<string, string>
}

interface FormData {
  website: string
  requester_name: string
  requester_email: string
  submitter_type: string
  legal_basis: string
  details: string
  attestation_perjury: boolean
  attestation_irreversible: boolean
  attestation_email_validation: boolean
  website_url: string // honeypot
}

export default function NewDsarRequestPage({
  legal_basis_options,
  details_max_length,
  values,
  errors: serverErrors,
}: Props) {
  const { data, setData, post, processing, errors } = useForm<FormData>({
    website: values?.website ?? '',
    requester_name: values?.requester_name ?? '',
    requester_email: values?.requester_email ?? '',
    submitter_type: values?.submitter_type ?? '',
    legal_basis: values?.legal_basis ?? '',
    details: values?.details ?? '',
    attestation_perjury: values?.attestation_perjury ?? false,
    attestation_irreversible: values?.attestation_irreversible ?? false,
    attestation_email_validation: values?.attestation_email_validation ?? false,
    website_url: '',
  })

  // Server-side errors from a prior submission (via Inertia props).
  const allErrors = { ...(serverErrors ?? {}), ...errors }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    post('/privacy/dsar')
  }

  const requiredFilled =
    data.website.trim() !== '' &&
    data.requester_name.trim() !== '' &&
    data.requester_email.trim() !== '' &&
    data.submitter_type !== '' &&
    data.legal_basis !== ''

  const allAttested =
    data.attestation_perjury &&
    data.attestation_irreversible &&
    data.attestation_email_validation

  const canSubmit = requiredFilled && allAttested && !processing

  const detailsRemaining = details_max_length - data.details.length

  return (
    <>
      <SeoHead
        title="Data Subject Access Request — LendSolo"
        description="Submit a privacy-law data request to LendSolo. We respond within the timeframe required by applicable law."
        canonicalPath="/privacy/dsar"
        ogType="article"
      />

      <PublicLayout
        title="Data Subject Access Request Form"
        description="Please fill in the information below. The website administrator or data protection officer will be notified of your request within 24 hours, and will need an appropriate amount of time to respond."
      >
        <div className="max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-900 mb-1">
                Website
              </label>
              <input
                id="website"
                type="text"
                value={data.website}
                onChange={(e) => setData('website', e.target.value)}
                placeholder="lendsolo.com"
                required
                className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {allErrors.website && (
                <p className="mt-1 text-sm text-red-600">{allErrors.website}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="requester_name" className="block text-sm font-medium text-gray-900 mb-1">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                id="requester_name"
                type="text"
                value={data.requester_name}
                onChange={(e) => setData('requester_name', e.target.value)}
                required
                className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {allErrors.requester_name && (
                <p className="mt-1 text-sm text-red-600">{allErrors.requester_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="requester_email" className="block text-sm font-medium text-gray-900 mb-1">
                What email address do you use to access the above website / app? <span className="text-red-500">*</span>
              </label>
              <input
                id="requester_email"
                type="email"
                value={data.requester_email}
                onChange={(e) => setData('requester_email', e.target.value)}
                required
                autoComplete="email"
                className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {allErrors.requester_email && (
                <p className="mt-1 text-sm text-red-600">{allErrors.requester_email}</p>
              )}
            </div>

            {/* Submitter type (radio) */}
            <fieldset>
              <legend className="block text-sm font-medium text-gray-900 mb-2">
                You are submitting this request as <span className="text-red-500">*</span>
              </legend>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="submitter_type"
                    value="self"
                    checked={data.submitter_type === 'self'}
                    onChange={(e) => setData('submitter_type', e.target.value)}
                    className="mt-1 text-emerald-600 focus:ring-emerald-500"
                    required
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    The person, or the parent / guardian of the person, whose name appears above.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="submitter_type"
                    value="authorized_agent"
                    checked={data.submitter_type === 'authorized_agent'}
                    onChange={(e) => setData('submitter_type', e.target.value)}
                    className="mt-1 text-emerald-600 focus:ring-emerald-500"
                    required
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    An agent authorized by the consumer to make this request on their behalf.
                  </span>
                </label>
              </div>
              {allErrors.submitter_type && (
                <p className="mt-1 text-sm text-red-600">{allErrors.submitter_type}</p>
              )}
            </fieldset>

            {/* Legal basis */}
            <div>
              <label htmlFor="legal_basis" className="block text-sm font-medium text-gray-900 mb-1">
                Under the rights of which law are you making this request? <span className="text-red-500">*</span>
              </label>
              <select
                id="legal_basis"
                value={data.legal_basis}
                onChange={(e) => setData('legal_basis', e.target.value)}
                required
                className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select...</option>
                {Object.entries(legal_basis_options).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {allErrors.legal_basis && (
                <p className="mt-1 text-sm text-red-600">{allErrors.legal_basis}</p>
              )}
            </div>

            {/* Details */}
            <div>
              <label htmlFor="details" className="block text-sm font-medium text-gray-900 mb-1">
                Please leave details regarding your action request or question.
              </label>
              <textarea
                id="details"
                value={data.details}
                onChange={(e) =>
                  setData(
                    'details',
                    e.target.value.length > details_max_length
                      ? e.target.value.slice(0, details_max_length)
                      : e.target.value
                  )
                }
                rows={5}
                maxLength={details_max_length}
                className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>
                  {allErrors.details && <span className="text-red-600">{allErrors.details}</span>}
                </span>
                <span className={detailsRemaining < 100 ? 'text-amber-600' : ''}>
                  {data.details.length.toLocaleString()} / {details_max_length.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Attestations */}
            <fieldset className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <legend className="px-2 text-sm font-medium text-gray-900">
                I confirm that <span className="text-red-500">*</span>
              </legend>
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.attestation_perjury}
                    onChange={(e) => setData('attestation_perjury', e.target.checked)}
                    className="mt-1 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    Under penalty of perjury, I declare all the above information to be true and accurate.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.attestation_irreversible}
                    onChange={(e) => setData('attestation_irreversible', e.target.checked)}
                    className="mt-1 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I understand that the deletion or restriction of my personal data is irreversible and may result in the termination of services with LendSolo.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.attestation_email_validation}
                    onChange={(e) => setData('attestation_email_validation', e.target.checked)}
                    className="mt-1 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I understand that I will be required to validate my request by email, and I may be contacted in order to complete the request.
                  </span>
                </label>
              </div>
              {(allErrors.attestation_perjury ||
                allErrors.attestation_irreversible ||
                allErrors.attestation_email_validation) && (
                <p className="mt-2 text-sm text-red-600">
                  All three statements must be checked to submit this request.
                </p>
              )}
            </fieldset>

            {/* Honeypot — hidden from humans, visible to naive bots */}
            <div
              style={{ position: 'absolute', left: '-9999px' }}
              aria-hidden="true"
            >
              <label htmlFor="website_url">Website URL</label>
              <input
                id="website_url"
                type="text"
                name="website_url"
                value={data.website_url}
                onChange={(e) => setData('website_url', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold tracking-wide text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'SUBMITTING…' : 'SUBMIT'}
              </button>
              <p className="mt-3 text-xs text-gray-500">
                Need help? Email{' '}
                <a
                  href="mailto:support@lendsolo.com"
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  support@lendsolo.com
                </a>
                , or see our{' '}
                <Link href="/privacy" className="text-emerald-600 hover:text-emerald-700">
                  Privacy Policy
                </Link>{' '}
                or{' '}
                <Link href="/terms" className="text-emerald-600 hover:text-emerald-700">
                  Terms of Service
                </Link>
                .
              </p>
            </div>
          </form>
        </div>
      </PublicLayout>
    </>
  )
}
